import { Injectable, inject, signal, computed } from '@angular/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ProjectService } from '../project/project.service';
import { NotificationWebsocketService } from '../notification-websocket/notification-websocket.service';
import { AppStateService } from '../app-state/app-state.service';
import { LocalforageService } from '../localforage/localforage.service';
import { CurrentEditor } from '../../interfaces/notification.interface';
import type { CurrentEditorInfo } from '@app/shared/interfaces/rt-messaging.generated';

export interface ProjectLockState {
  isLocked: boolean;
  isCurrentEditor: boolean;
  projectLockedBy: string | null;
  currentEditor: CurrentEditor | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectLockService {
  private readonly projectService = inject(ProjectService);
  private readonly notificationWebsocketService = inject(NotificationWebsocketService);
  private readonly appStateService = inject(AppStateService);
  private readonly toastr = inject(ToastrService);
  private readonly localforageService = inject(LocalforageService);

  // State signals
  readonly #lockState = signal<ProjectLockState>({
    isLocked: false,
    isCurrentEditor: false,
    projectLockedBy: null,
    currentEditor: null,
  });

  // Public readonly signals
  readonly lockState = this.#lockState.asReadonly();
  readonly isLocked = computed(() => this.#lockState().isLocked);
  readonly isCurrentEditor = computed(() => this.#lockState().isCurrentEditor);
  readonly projectLockedBy = computed(() => this.#lockState().projectLockedBy);
  readonly currentEditor = computed(() => this.#lockState().currentEditor);

  private notificationSubscription?: Subscription;
  private currentProjectId: number | null = null;
  private wasAutoLocked = false;
  private lockStatusChecked = false;

  /**
   * Initialize lock service for a project
   */
  initialize(projectId: number): void {
    this.currentProjectId = projectId;
    this.setupWebSocket();
    this.checkProjectLocked(projectId);
  }

  /**
   * Cleanup when leaving a project
   */
  cleanup(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = undefined;
    }
    this.currentProjectId = null;
    this.wasAutoLocked = false;
    this.lockStatusChecked = false;
    this.#lockState.set({
      isLocked: false,
      isCurrentEditor: false,
      projectLockedBy: null,
      currentEditor: null,
    });
  }

  /**
   * Setup WebSocket subscription for editor changes
   */
  private setupWebSocket(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    this.notificationSubscription = this.notificationWebsocketService
      .receiveMessage()
      .subscribe((data) => {
        // Handle current-editor messages (both locked and unlocked states)
        // When unlocked, editor object exists but userId/username/nickname are null/undefined
        if (data.message === 'current-editor' && 'editor' in data && data.editor && typeof data.editor === 'object') {
          const editorInfo = data.editor as CurrentEditorInfo;
          // Ensure projectId exists (required field)
          if (editorInfo && 'projectId' in editorInfo && typeof editorInfo.projectId === 'number') {
            const editor: CurrentEditor = {
              projectId: editorInfo.projectId,
              userId: editorInfo.userId ?? null,
              username: editorInfo.username ?? null,
              nickname: editorInfo.nickname ?? null,
            };
            this.handleCurrentEditorMessage(editor);
          }
        }
      });
  }

  /**
   * Handle current editor WebSocket message
   */
  private handleCurrentEditorMessage(editor: CurrentEditor): void {
    if (!this.currentProjectId || editor.projectId !== this.currentProjectId) {
      return;
    }

    const wasLocked = this.#lockState().isLocked;
    const wasCurrentEditor = this.#lockState().isCurrentEditor;

    const currentUser = this.appStateService.user();
    const isCurrentEditor = editor.userId !== null && editor.userId === currentUser?.userId;
    // Project is locked if userId is not null and not undefined
    const isLocked = editor.userId != null;

    this.#lockState.set({
      isLocked,
      isCurrentEditor,
      // Clear projectLockedBy when unlocked (userId is null/undefined)
      projectLockedBy: isLocked ? editor.username : null,
      currentEditor: editor,
    });

    // Update AppStateService currentEditor signal
    this.appStateService.currentEditor.set(editor);

    // Update cached project data
    this.updateCachedProjectLockStatus(editor.userId);

    // Show notifications based on state changes
    const isAutoLockConfirmation = this.wasAutoLocked && 
                                   isLocked && 
                                   isCurrentEditor && 
                                   !wasLocked;

    if (isLocked && !isCurrentEditor && (!wasLocked || !wasCurrentEditor)) {
      this.toastr.info(
        `Project is locked by ${editor.username}`,
        'Project Locked'
      );
    } else if (isLocked && isCurrentEditor && (!wasLocked || !wasCurrentEditor) && !isAutoLockConfirmation) {
      this.toastr.success(
        'You can now add workflows, edit steps, and make other changes.',
        'Project Locked for Editing'
      );
    } else if (!isLocked && wasLocked) {
      this.toastr.info(
        'Lock the project to add workflows, edit steps, and make changes.',
        'Project Unlocked'
      );
    }
  }

  /**
   * Update cached project data to reflect new lock status
   */
  private updateCachedProjectLockStatus(currentEditorId: number | null): void {
    if (!this.currentProjectId) return;

    this.projectService.getProjectFromStorage(this.currentProjectId).subscribe({
      next: (cachedProject) => {
        if (cachedProject) {
          cachedProject.currentEditorId = currentEditorId;
          const storageKey = `project_${this.currentProjectId}`;
          this.localforageService.setItem(storageKey, cachedProject)
            .catch(error => console.error('Error updating cached project lock status:', error));
        }
      },
      error: (error) => {
        console.error('Error updating cached project lock status:', error);
      }
    });
  }

  /**
   * Check project lock status via REST API
   */
  checkProjectLocked(projectId: number): void {
    this.projectService.getProjectLock(projectId).subscribe({
      next: (lockData) => {
        const currentUser = this.appStateService.user();
        const currentEditor: CurrentEditor = lockData.holder
          ? {
              projectId,
              userId: lockData.holder.userId,
              username: lockData.holder.name,
              nickname: lockData.holder.nickname,
            }
          : {
              projectId,
              userId: null,
              username: null,
              nickname: null,
            };

        const isCurrentEditor = lockData.holder?.userId === currentUser?.userId;

        this.#lockState.set({
          isLocked: lockData.locked,
          isCurrentEditor,
          projectLockedBy: lockData.holder?.name ?? null,
          currentEditor,
        });

        // Update AppStateService currentEditor signal
        this.appStateService.currentEditor.set(currentEditor);

        this.lockStatusChecked = true;
      },
      error: (error) => {
        console.error('Error checking project lock status:', error);
        // Fallback to WebSocket method only if REST API fails
        this.notificationWebsocketService
          .getCurrentEditor(projectId)
          .then(() => { })
          .catch(() => { });
      }
    });
  }

  /**
   * Lock the project
   */
  lockProject(projectId: number, force = false): Promise<void> {
    // Use WebSocket for regular lock (better real-time sync)
    // Use HTTP API for force lock (WebSocket doesn't support force parameter)
    if (force) {
      return firstValueFrom(
        this.projectService.setProjectLock(projectId, { force })
      ).then(() => {
        // Update local state immediately
        const currentUser = this.appStateService.user();
        const currentEditor: CurrentEditor = {
          projectId,
          userId: currentUser?.userId ?? null,
          username: currentUser?.name ?? null,
          nickname: currentUser?.nickname ?? null,
        };

        this.#lockState.set({
          isLocked: true,
          isCurrentEditor: true,
          projectLockedBy: currentUser?.name ?? null,
          currentEditor,
        });

        this.appStateService.currentEditor.set(currentEditor);
        this.updateCachedProjectLockStatus(currentUser?.userId ?? null);
        this.wasAutoLocked = false; // Manual lock
      });
    } else {
      // Use WebSocket lock-editor command for regular lock
      // This ensures better real-time synchronization via WebSocket
      return this.notificationWebsocketService
        .lockEditor(projectId)
        .then(() => {
          // Update local state immediately (WebSocket will also send push notification)
          const currentUser = this.appStateService.user();
          const currentEditor: CurrentEditor = {
            projectId,
            userId: currentUser?.userId ?? null,
            username: currentUser?.name ?? null,
            nickname: currentUser?.nickname ?? null,
          };

          this.#lockState.set({
            isLocked: true,
            isCurrentEditor: true,
            projectLockedBy: currentUser?.name ?? null,
            currentEditor,
          });

          this.appStateService.currentEditor.set(currentEditor);
          this.updateCachedProjectLockStatus(currentUser?.userId ?? null);
          this.wasAutoLocked = false; // Manual lock
        });
    }
  }

  /**
   * Unlock the project
   */
  unlockProject(projectId: number, force = false): Promise<void> {
    // Use WebSocket for regular unlock (better real-time sync)
    // Use HTTP API for force unlock (WebSocket doesn't support force parameter)
    if (force) {
      return firstValueFrom(
        this.projectService.setProjectLock(projectId, {
          unlockOnly: true,
          force,
        })
      ).then(() => {
        // Update local state immediately
        const currentEditor: CurrentEditor = {
          projectId,
          userId: null,
          username: null,
          nickname: null,
        };

        this.#lockState.set({
          isLocked: false,
          isCurrentEditor: false,
          projectLockedBy: null,
          currentEditor,
        });

        this.appStateService.currentEditor.set(currentEditor);
        this.updateCachedProjectLockStatus(null);
      });
    } else {
      // Use WebSocket unlock-editor command for regular unlock
      // This ensures better real-time synchronization via WebSocket
      return this.notificationWebsocketService
        .unlockEditor(projectId)
        .then(() => {
          // Update local state immediately (WebSocket will also send push notification)
          const currentEditor: CurrentEditor = {
            projectId,
            userId: null,
            username: null,
            nickname: null,
          };

          this.#lockState.set({
            isLocked: false,
            isCurrentEditor: false,
            projectLockedBy: null,
            currentEditor,
          });

          this.appStateService.currentEditor.set(currentEditor);
          this.updateCachedProjectLockStatus(null);
        });
    }
  }

  /**
   * Toggle lock state
   */
  toggleLock(projectId: number): void {
    if (this.#lockState().isLocked) {
      const force = this.shouldUseForceUnlock();
      this.unlockProject(projectId, force).catch((error) => {
        this.toastr.error('Error unlocking project', 'Cannot Unlock Project');
        console.error('Error unlocking project:', error);
      });
    } else {
      this.lockProject(projectId, false).catch((error) => {
        this.toastr.error('Error locking project', 'Cannot Lock Project');
        console.error('Error locking project:', error);
      });
    }
  }

  /**
   * Check if user can perform lock operations
   */
  canUserPerformLockOperations(): boolean {
    return this.projectService.canPerformLockOperations();
  }

  /**
   * Check if user can unlock the project
   */
  canUserUnlockProject(): boolean {
    return this.#lockState().isCurrentEditor || this.projectService.canForceUnlock();
  }

  /**
   * Determine if force unlock should be used
   */
  shouldUseForceUnlock(): boolean {
    return !this.#lockState().isCurrentEditor && this.projectService.canForceUnlock();
  }

  /**
   * Set auto-locked flag
   */
  setWasAutoLocked(value: boolean): void {
    this.wasAutoLocked = value;
  }

  /**
   * Get auto-locked flag
   */
  getWasAutoLocked(): boolean {
    return this.wasAutoLocked;
  }

  /**
   * Check if lock status has been checked
   */
  isLockStatusChecked(): boolean {
    return this.lockStatusChecked;
  }
}

